import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, XCircle } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import api from "../lib/api";
import { logger } from "../lib/logger";
import { useLabText } from "../lib/labText";

const MAX_TRIES = 30;

export default function PaymentSuccess() {
  const { t } = useLang();
  const { clearCart } = useCart();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking, timeout, error
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          if (data.kind === "lab") { navigate(`/experiments?paid=${data.order_number}`, { replace: true }); return; }
          clearCart();
          navigate(`/order/${data.order_number}`, { replace: true });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) {
        logger.error("payment status check failed", e);
        if (!active) return;
      }
      tries.current += 1;
      if (tries.current >= MAX_TRIES) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clearCart, navigate]);

  return (
    <div className="max-w-md mx-auto px-5 py-32 text-center">
      {state === "checking" && (
        <>
          <Loader2 size={40} className="text-[#C49A6C] animate-spin mx-auto" strokeWidth={1.4} />
          <h1 className="serif text-3xl mt-8">{t("verifying_payment")}</h1>
          <p className="text-[#8A857D] mt-4">{t("please_wait")}</p>
        </>
      )}
      {state !== "checking" && (
        <>
          <h1 className="serif text-3xl">{t("payment_processing")}</h1>
          <p className="text-[#8A857D] mt-4">{t("payment_processing_note")}</p>
          <Link to="/account" className="inline-block mt-8 text-xs uppercase tracking-[0.2em] text-[#C49A6C] link-underline">{t("orders")} →</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const { t: shopT } = useLang();
  const { t: labT } = useLabText();
  const [params] = useSearchParams();
  const order = params.get("order");
  const lab = params.get("lab") === "1";
  const t = (k) => (lab ? { payment_cancelled: labT("cancel_title"), payment_cancelled_note: labT("cancel_note"), checkout: labT("back_lab") }[k] || shopT(k) : shopT(k));
  useEffect(() => {
    if (lab && order) api.post(`/lab/orders/${order}/cancel`).catch((e) => logger.error("lab cancel failed", e));
  }, [lab, order]);
  return (
    <div className="max-w-md mx-auto px-5 py-32 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 border border-[#5a2a2a] mb-8">
        <XCircle size={28} className="text-[#c88]" strokeWidth={1.4} />
      </div>
      <h1 className="serif text-3xl">{t("payment_cancelled")}</h1>
      <p className="text-[#8A857D] mt-4">{t("payment_cancelled_note")}</p>
      <div className="flex flex-col gap-3 mt-8">
        <Link to={lab ? "/experiments" : "/checkout"} data-testid="payment-cancel-retry" className="bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#E7CBA9] transition-colors">{t("checkout")}</Link>
        <Link to="/shop" className="text-xs uppercase tracking-[0.2em] text-[#8A857D] hover:text-[#F2EBE1] transition-colors">{t("continue_shopping")}</Link>
      </div>
    </div>
  );
}
